/**
 * Shared class strings for the Playground's editorial register: small tracked
 * labels, underlined serif fields, and text-only actions. Kept in one place
 * so the control panels and result views agree with the rest of the site.
 */

/** Thin rectangular frame around a panel: the controls, the output, the graph. */
export const PANEL = "rounded-[3px] border border-zinc-900/[0.14] px-6 py-5";

/** Tinted title band across the top of a PANEL (bleeds to the frame edges).
 * Gives each panel a visible head so the page reads as parts, not prose. */
export const PANEL_HEAD = "-mx-6 -mt-5 border-b border-zinc-900/[0.10] bg-zinc-900/[0.03] px-6 py-3";

/** Hairline between sections inside a panel. */
export const PANEL_RULE = "border-t border-zinc-900/[0.08] pt-5";

/** Small caps label above a field or section. */
export const LABEL = "font-ui block text-zinc-400";

/** Serif input drawn as a rule beneath the text rather than a box. */
export const FIELD = "pg-field w-full bg-transparent text-base leading-7 text-zinc-900 placeholder:italic placeholder:text-zinc-400";

/** Mono variant of the field for identifiers and regexes. */
export const FIELD_MONO = "pg-field w-full bg-transparent font-mono text-sm leading-7 text-zinc-900 placeholder:font-serif placeholder:italic placeholder:text-base placeholder:text-zinc-400";

/** Quiet running text action (Copy, Grep this paper, ...). */
export const TEXT_ACTION = "font-ui text-sm text-zinc-400 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:text-zinc-900 disabled:pointer-events-none disabled:text-zinc-300";

/** A choice in a running line of alternatives; pass `active` to set it in ink. */
export const choice = (active: boolean) =>
  `font-ui text-base leading-6 transition-colors focus-visible:outline-none ${active
    ? "text-zinc-900 underline decoration-zinc-400 underline-offset-[5px]"
    : "text-zinc-400 hover:text-zinc-800"
  }`;

/** Hairline separator between choices. */
export const DOT = "select-none text-zinc-300";

/** Footnote-style helper copy under a field. */
export const HELP = "mt-1.5 text-sm leading-5 text-zinc-400";

/** Meta line under a result title: authors · year · source · id. */
export const META = "text-sm leading-5 text-zinc-500";

/** The one filled control on the page: Run / Sign in to run. */
export const RUN_BUTTON =
  "font-ui rounded-[3px] bg-zinc-900 px-4 py-1 text-base leading-6 text-white transition-opacity hover:opacity-90 disabled:opacity-30";

/** Small mono chip for identifiers, sources, counts. */
export const CHIP = "rounded-[2px] bg-zinc-100 px-1.5 py-0.5 font-mono text-2xs text-zinc-600";
